import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../sessionSlice";
import { loginUser, updateUser } from "../loginSlice";
import { fetchModakStatus } from "./modakApi";
import { useModakStorageKey } from "./useElapsedTimer";

/**
 * Shared bootstrap for every Modak Marathon screen: waits for the login
 * bootstrap (loginSlice) to populate `state.auth.user`, sends it to
 * /modakStatus, and — for round screens — redirects home if this round (or,
 * for round 1-4, the *next* incomplete round) doesn't match, mirroring the
 * old PHP roundN.php "you have played already" / auto-redirect checks.
 *
 * A refresh on any of these pages remounts React with an empty Redux store,
 * so `user` starts out null here too — rather than bouncing straight to
 * /login (losing the player's spot), this resumes the session in place via
 * loginUser() (which now resumes from sessionStorage on its own) and only
 * redirects to /login if that resume genuinely fails (no saved session at all).
 */
export function useRoundGate(roundNumber) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const authStatus = useSelector((state) => state.auth.status);
  const adminToken = useSelector(selectAdminToken);
  const timerKey = useModakStorageKey(user?.sessionId, user?.userId);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      if (authStatus === "idle") {
        dispatch(loginUser());
      } else if (authStatus === "failed") {
        navigate("/login", { replace: true });
      }
      return;
    }
    if (!adminToken) return;

    let cancelled = false;
    fetchModakStatus(adminToken, {
      userId: user.userId,
      email: user.email,
      name: user.name,
      userType: user.role,
    }).then((res) => {
      if (cancelled) return;
      if (!res?.success) {
        setLoading(false);
        return;
      }
      setStatus(res);

      // Keep session reportId / gameover in sync with DB (reportId stays null
      // until sendReport links one; gameover flips when all 5 rounds are done).
      //
      // The dirty-check here MUST be against the current Redux `user`, not
      // sessionStorage — pushModakExternalReport (modakApi.js) already writes
      // a freshly-linked reportId straight into sessionStorage the moment it
      // gets one, in the *same* round. So by the time the *next* round mounts
      // and lands here, sessionStorage already agrees with the DB, `changed`
      // would come back false against it, and the dispatch that's supposed to
      // push the reportId into Redux never fires — leaving `user.reportId` in
      // Redux permanently stale (usually undefined) for the rest of the
      // session, so every later round keeps POSTing "add" instead of PUT
      // "update". Comparing against Redux's own `user` instead closes that gap.
      try {
        let changed = false;
        const userPatch = {};
        if (res.reportId && user.reportId !== res.reportId) {
          userPatch.reportId = String(res.reportId).trim();
          changed = true;
        }
        if (res.gameover != null && Number(user.gameover ?? 0) !== Number(res.gameover)) {
          userPatch.gameover = Number(res.gameover) || 0;
          changed = true;
        }
        if (res.status && user.status !== res.status) {
          userPatch.status = res.status;
          changed = true;
        }
        if (changed) {
          dispatch(updateUser(userPatch));
          const stored = JSON.parse(sessionStorage.getItem("userData") || "{}");
          const merged = { ...stored, ...userPatch };
          delete merged.report_id;
          sessionStorage.setItem("userData", JSON.stringify(merged));
        }
      } catch (_) {
        /* ignore */
      }

      if (roundNumber != null) {
        if (res.rounds?.[roundNumber]?.done) {
          Swal.fire("You have played already", "", "success").then(() => {
            navigate("/home", { replace: true });
          });
          return;
        }
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, adminToken, authStatus, dispatch]);

  return { user, adminToken, status, loading, timerKey };
}
